import { useMemo } from "react";
import type { Account, DashboardData, Trip } from "../types";
import { isSpendable, reportableBase, todayIn } from "../lib/finance";
import { calculateTripPacing, type TripPacing } from "../lib/trips";
import { getEffectiveRates } from "../lib/fx-rates";

export type { TripPacing };
export { calculateTripPacing };

export interface CategorySpendStat {
  category: string;
  amountBase: number;
  amountBudget: number;
  count: number;
  percent: number;
}

export interface DestinationSpendStat {
  destination: string;
  amountBase: number;
  amountBudget: number;
  count: number;
  percent: number;
}

export interface ProfileSpendStat {
  owner: string;
  amountBase: number;
  amountBudget: number;
  count: number;
  percent: number;
}

export interface WalletStat {
  account: Account;
  balance: number;
  currency: string;
  spent: number;
  spentBase: number;
  spentBudget: number;
  totalFunds: number;
  percentSpent: number;
}

export function computeTripStats(
  trip: Trip | null | undefined,
  data: DashboardData,
) {
  if (!trip) {
    return null;
  }

  const transactions = data.transactions || [];
  const accounts = data.accounts || [];
  const rates = data.rates || {};
  const baseCurrency = data.baseCurrency || "MVR";

  const effectiveRates = getEffectiveRates(baseCurrency, rates);

    const toBase = (t: { currency: string; amount: number; reportableAmount?: number | null; amountBase?: number | null; reportableAmountBase?: number | null }): number => {
      const authoritative = reportableBase({ amountBase: t.amountBase ?? null, reportableAmountBase: t.reportableAmountBase });
      if (authoritative !== null && authoritative !== undefined) return authoritative;
      const raw = t.reportableAmount ?? t.amount ?? 0;
      const r = effectiveRates[t.currency] ?? 1;
      return raw * r;
    };

    const tripTransactions = transactions
      .filter((t) => t.tripId === trip.id)
      .sort((a, b) => {
        const d = b.date.localeCompare(a.date);
        if (d !== 0) return d;
        return (b.time || "").localeCompare(a.time || "");
      });

    const budgetCurrency = trip.budgetCurrency || baseCurrency;
    const rateToBudget =
      budgetCurrency === baseCurrency
        ? 1
        : effectiveRates[budgetCurrency]
          ? 1 / effectiveRates[budgetCurrency]
          : 1;

    let totalSpendBase = 0;
    let totalIncomeBase = 0;
    let totalTransfersCount = 0;
    const catMap = new Map<string, { amountBase: number; count: number }>();
    const destMap = new Map<string, { amountBase: number; count: number }>();
    const ownerMap = new Map<string, { amountBase: number; count: number }>();

    for (const t of tripTransactions) {
      if (t.type === "Income") {
        const amtBase = toBase(t);
        totalIncomeBase += amtBase;
        continue;
      }
      if (t.type === "Transfer") {
        totalTransfersCount++;
        continue;
      }
      if (!isSpendable(t)) continue;
      const amtBase = toBase(t);
      totalSpendBase += amtBase;

      // Category breakdown
      const cat = t.category || "Uncategorized";
      const existingCat = catMap.get(cat) || { amountBase: 0, count: 0 };
      catMap.set(cat, {
        amountBase: existingCat.amountBase + amtBase,
        count: existingCat.count + 1,
      });

      // Destination breakdown
      const dest = t.tripDestination || (trip.destinations.length === 1 ? trip.destinations[0] : "General");
      const existingDest = destMap.get(dest) || { amountBase: 0, count: 0 };
      destMap.set(dest, {
        amountBase: existingDest.amountBase + amtBase,
        count: existingDest.count + 1,
      });

      // Traveler breakdown
      const owner = t.owner || "Household";
      const existingOwner = ownerMap.get(owner) || { amountBase: 0, count: 0 };
      ownerMap.set(owner, {
        amountBase: existingOwner.amountBase + amtBase,
        count: existingOwner.count + 1,
      });
    }

    const totalSpendBudget = totalSpendBase * rateToBudget;
    const totalIncomeBudget = totalIncomeBase * rateToBudget;
    const budget = trip.budget;
    const remainingBudget = budget !== null ? budget - totalSpendBudget : null;
    const budgetPercent =
      budget && budget > 0
        ? Math.min(100, Math.round((totalSpendBudget / budget) * 100))
        : 0;
    const isOverBudget = budget !== null && totalSpendBudget > budget;

    const categoryBreakdown: CategorySpendStat[] = Array.from(catMap.entries())
      .map(([category, val]) => ({
        category,
        amountBase: val.amountBase,
        amountBudget: val.amountBase * rateToBudget,
        count: val.count,
        percent: totalSpendBase > 0 ? Math.round((val.amountBase / totalSpendBase) * 100) : 0,
      }))
      .sort((a, b) => b.amountBase - a.amountBase);

    const destinationBreakdown: DestinationSpendStat[] = Array.from(destMap.entries())
      .map(([destination, val]) => ({
        destination,
        amountBase: val.amountBase,
        amountBudget: val.amountBase * rateToBudget,
        count: val.count,
        percent: totalSpendBase > 0 ? Math.round((val.amountBase / totalSpendBase) * 100) : 0,
      }))
      .sort((a, b) => b.amountBase - a.amountBase);

    const profileBreakdown: ProfileSpendStat[] = Array.from(ownerMap.entries())
      .map(([owner, val]) => ({
        owner,
        amountBase: val.amountBase,
        amountBudget: val.amountBase * rateToBudget,
        count: val.count,
        percent: totalSpendBase > 0 ? Math.round((val.amountBase / totalSpendBase) * 100) : 0,
      }))
      .sort((a, b) => b.amountBase - a.amountBase);

    const linkedIds = new Set(trip.accountIds || []);
    for (const t of tripTransactions) {
      if (t.accountId && isSpendable(t)) linkedIds.add(t.accountId);
    }
    const linkedAccounts = Array.from(linkedIds)
      .map((id) => accounts.find((a) => a.id === id))
      .filter((a): a is Account => !!a);

    const walletBalances: WalletStat[] = linkedAccounts.map((account) => {
      const accTxs = tripTransactions.filter((t) => t.accountId === account.id && isSpendable(t));
      let spent = 0;
      let spentBase = 0;
      for (const t of accTxs) {
        const raw = t.reportableAmount ?? t.amount ?? 0;
        spent += raw;
        spentBase += toBase(t);
      }
      const spentBudget = spentBase * rateToBudget;
      const totalFunds = account.balance > 0 ? account.balance + spent : spent;
      const percentSpent = totalFunds > 0 ? Math.min(100, Math.round((spent / totalFunds) * 100)) : (spent > 0 ? 100 : 0);
      return {
        account,
        balance: account.balance,
        currency: account.currency,
        spent,
        spentBase,
        spentBudget,
        totalFunds,
        percentSpent,
      };
    });

    const today = todayIn(data.timezone);
    const pacing = calculateTripPacing(
      trip.startDate,
      trip.endDate,
      today,
      totalSpendBudget,
      budget,
      trip.status,
    );

    return {
      tripTransactions,
      totalSpendBase,
      totalSpendBudget,
      totalIncomeBase,
      totalIncomeBudget,
      totalTransfersCount,
      budgetCurrency,
      budget,
      remainingBudget,
      budgetPercent,
      isOverBudget,
      categoryBreakdown,
      destinationBreakdown,
      profileBreakdown,
      walletBalances,
      pacing,
    };
}

export function useTripStats(
  trip: Trip | null | undefined,
  data: DashboardData,
) {
  const transactions = useMemo(() => data.transactions || [], [data.transactions]);
  const accounts = useMemo(() => data.accounts || [], [data.accounts]);
  const rates = useMemo(() => data.rates || {}, [data.rates]);
  const baseCurrency = data.baseCurrency || "MVR";
  const timezone = data.timezone;

  return useMemo(() => {
    return computeTripStats(trip, {
      ...data,
      transactions,
      accounts,
      rates,
      baseCurrency,
      timezone,
    });
  }, [trip, transactions, accounts, rates, baseCurrency, timezone, data]);
}
